import React, { useEffect, useState } from "react";
import RequestsHeader from "../../components/DataRequests/RequestsHeader/RequestsHeader";
import RequestTabs from "../../components/DataRequests/RequestsTabs/RequestTabs";
import RequestsTable from "../../components/DataRequests/RequestsTable/RequestsTable";
import { filterDataByStatus } from "../../components/snapshots/Utils/FilterFunctions";
import { getAllDataRequests } from "../../components/s3/GetDataRequests";
import Loading from "../../components/common/Loading/Loading";
import { formatDataToJSON } from "../../components/s3/DataRequest/FormatDataToJSON";
import { saveJsonToS3Bucket } from "../../components/s3/DataRequest/SaveJSONToS3";
import { v4 as uuidv4 } from "uuid";
import { useNavigate, useOutletContext } from "react-router-dom";
import WarningModal from "../../components/Modals/WarningModal/WarningModal";
import { SendMessage } from "../../components/WhatsApp/SendMessage";
import MessageResultModal from "../../components/Modals/MessageResultModal/MessageResultModal";

/**
 * Data requests page
 */
export default function DataRequests() {
  const [toggleState, setToggleState] = useState("All");
  const [data, setData] = useState([]); //data stored in S3
  const [newData, setNewData] = useState([]); //data created by the user through New Requests
  const [spinner, setSpinner] = useState(true);
  const [modalWarningOpen, setModalWarningOpen] = useState(false); //modal to warn user if new unsent properties exist before skipped to another page
  const [messageResultModalOpen, setMessageResultModalOpen] = useState(false); //modal that shows if the attemp to send a WhatsApp message was successful or failed
  const [messageResultStatus, setMessageResultStatus] = useState(null); //WhatsApp message sent status
  const navigate = useNavigate();
  const getS3 = useOutletContext().getS3;
  const [S3, setS3] = useState(null);
  const company = localStorage.getItem("company");
  const currentSnapshot = localStorage.getItem("selectedSnapshot");

  useEffect(() => {
    getS3().then(setS3);
  }, [])

  //Get Data Requests from S3
  useEffect(() => {
    if(!S3) return;
    async function fetchData() {
      setSpinner(true);
      setData([]);
      try {
        const s3Data = await getAllDataRequests(S3);
        // console.log("Fetched S3 Data:", s3Data);
        setData(s3Data);
      } catch (error) {
        // console.error("Error fetching data:", error);
      }
      setSpinner(false);
    }
    fetchData();
  }, [S3]);

  // Enable toggle functionality for tabs (active tab)
  const toggleTab = (status) => {
    setToggleState(status);
  };

  // Filters and sorts data according to the status
  const filteredData = [
    ...filterDataByStatus(toggleState, newData),
    ...filterDataByStatus(toggleState, data),
  ];

  //Handle a New Request
  const handleNewRequest = () => {
    const newRequest = {
      date: new Date().toLocaleString(),
      MemberName: "",
      Phone: "",
      AssignedProperties: "",
      requestformlink: "",
      status: "",
      isNew: true,
      isSubmitted: false,
      isMerged: false,
      SnapshotName: `${currentSnapshot}`
    };
    setNewData((prevNewData) => [...prevNewData, newRequest]);
  };

  //Create a new request
  const handleSendNewRequest = async () => {
    const newRequestIndex = newData.findIndex((row) => row.isNew);
    const newRequest = newData[newRequestIndex];
    if (!newRequest) return;

    const companyName = localStorage.getItem("companyName");
    const uniqueId = uuidv4(); // Generates a UUID
    //Filename is composed by snapshot name and unique id - this will be part of the link to access the file externally
    const fileName = `${companyName}-${uniqueId}`;

    if (newRequest) {
      console.log("new request: ", newRequest);
      const blob = formatDataToJSON(newRequest, fileName);
      let messageResult = {};
      try {
        const requestFormLink = await saveJsonToS3Bucket(
          S3,
          blob,
          fileName,
          newRequest,
          company,
          newRequest.isSubmitted,
          newRequest.isMerged,
        );
        console.log("File uploaded successfully");
        setNewData([]);
        // Refresh the data
        const s3Data = await getAllDataRequests(S3);
        setData(s3Data);

        //Send Message with the JSON file link to the client
        messageResult = await SendMessage(
          newRequest.MemberName,
          newRequest.Phone,
          requestFormLink
        );
        setMessageResultModalOpen(true);
        setMessageResultStatus(messageResult);
      } catch (error) {
        setMessageResultModalOpen(true);
        setMessageResultStatus(messageResult);
        console.error("Error uploading file to S3:", error);
      }
    }
  };

  // Handle ArrowBack click
  const handleArrowBackClick = () => {
    if (newData.some((row) => row.isNew)) {
      setModalWarningOpen(true);
    } else {
      const initialFileName = localStorage.getItem("initialFileName");
      navigate(`/inputform/${initialFileName}`);
    }
  };

  const handleNavigate = () => {
    setModalWarningOpen(false);
    const initialFileName = localStorage.getItem("initialFileName");
    navigate(`/inputform/${initialFileName}`);
  };

  // Send button is only enabled when there is a value to MemberName, Phone and AssignedProperties
  const isSendButtonEnabled = () => {
    return newData.some(
      (row) =>
        row.isNew && row.MemberName && row.Phone && row.AssignedProperties
    );
  };

  return (
    <div>
      <RequestsHeader
        handleNewRequest={handleNewRequest}
        handleSendNewRequest={handleSendNewRequest}
        handleArrowBackClick={handleArrowBackClick}
        isSendButtonEnabled={isSendButtonEnabled()}
      />
      <RequestTabs toggleState={toggleState} toggleTab={toggleTab} />
      {spinner ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            fontSize: "2rem",
            position: "absolute",
            right: "50%",
            top: "50%",
          }}
        >
          <Loading />
        </div>
      ) : !data.length && !newData.length ? (
        <div className="no-snapshots">
          You have not created any data requests yet. Click the ‘New Request’ button to send a data request.
        </div>
      ) : filteredData.length === 0 ? (
        <div className="no-snapshots">
          There are no{" "}
          {toggleState.toLowerCase() !== "all"
            ? toggleState.toLowerCase() + " "
            : ""}
          data requests. Please email contact@hecoanalytics.com if you think
          something is missing.
        </div>
      ) : (
        <RequestsTable
          data={filteredData}
          setData={setData}
          setNewData={setNewData}
        />
      )}
      {modalWarningOpen && (
        <WarningModal
          isOpen={modalWarningOpen}
          onClose={() => {
            setModalWarningOpen(false);
          }}
          onNavigate={handleNavigate}
        />
      )}
      {messageResultModalOpen && (
        <MessageResultModal
          isOpen={messageResultModalOpen}
          onClose={() => setMessageResultModalOpen(false)}
          messageResult={messageResultStatus}
        />
      )}
    </div>
  );
}
